import { GetterTree, Module, MutationTree } from 'vuex';
import { RootState } from '../rootState';
import { IShader } from '@/models/shaders/shader';
import { Shader6458 } from '@/models/shaders/furniture/shader_6458';
import { ShaderH3396 } from '@/models/shaders/furniture/shader_h3396';
import { ShaderH3325 } from '@/models/shaders/furniture/shader_h3325';
import { ShaderH3157 } from '@/models/shaders/furniture/shader_h3157';
import { ShaderU163 } from '@/models/shaders/furniture/shader_u163';
import { ShaderU599 } from '@/models/shaders/furniture/shader_u599';
import { ShaderU606 } from '@/models/shaders/furniture/shader_u606';
import { ShaderU830 } from '@/models/shaders/furniture/shader_u830';
import { ShaderU899 } from '@/models/shaders/furniture/shader_u899';
import { ShaderU8101 } from '@/models/shaders/furniture/shader_u8101';
import { ShaderH3162 } from '@/models/shaders/furniture/shader_h3162';
import { Handle, handles } from '@/models/handles';
import { legs } from '@/models/legs';
import { Shader01Rafix } from '@/models/shaders/others/shader_01_rafix';
import { Shader01Podp } from '@/models/shaders/others/shader_01_podp';
import { Shader02Podp } from '@/models/shaders/others/shader_02_podp';
import { Shader02Rafix } from '@/models/shaders/others/shader_02_rafix';
import { ElementMenuItem } from '@/models/elementMenuItem';
import { ElementType, SpecyficType } from '@/models/element';
import { ShaderDrazek } from '@/models/shaders/others/shader_drazek';
import { ShaderSzuflada } from '@/models/shaders/others/shader_szuflada';
import { ShaderZawias } from '@/models/shaders/others/shader_zawias';
import { ShaderNPodp } from '@/models/shaders/legs/shader_n_podp';
import { ShaderNBaza } from '@/models/shaders/legs/shader_n_baza';
import { ShadersPresetData } from '@/models/presets';
import store from '..';
import { Legs } from '@/models/legs/legs';
import { SectionType } from '@/models/itemSection';
import { Pair } from '@/models/pair';
import { ShaderLiftedFrame } from '@/models/shaders/bed/shader_lifted_frame';
import { ShaderFloorFrame } from '@/models/shaders/bed/shader_floor_frame';
import { ShaderNull } from '@/models/shaders/shader_null';
import { ShaderKeaRing } from '@/models/shaders/others/shader_kea_ring';
import { ShaderTasma } from '@/models/shaders/others/shader_tasma';
import { ShaderFloorWithFlexibleSlatsFrame } from '@/models/shaders/bed/shader_floor_with_flexible_slats';

export interface Shaders {
    furniture: IShader[];
    selectedHandle: Handle;
    selectedFront: IShader;
    selectedWall: IShader;
    selectedShelf: IShader;
    selectedBody: IShader;
    selectedLegs: Legs;
    selectedRafix: IShader;
    selectedSupport: IShader;
    selectedFrame: IShader;
}

const woodShaders = [
    'ShaderH3396',
    'ShaderH3162',
    'ShaderH3325',
    'ShaderH3157',
    'ShaderNWalec',
    'ShaderU06Siro',
    'ShaderNStScieta',
    'ShaderU09Siro',
    'ShaderNBox',
    'ShaderNStozek',
    'ShaderNStBox',
    'ShaderU01Siro',
];

const getters: GetterTree<Shaders, RootState> = {
    shaders(state): Shaders {
        return state;
    },
    shaderFor: (state) => (element: ElementMenuItem) => {
        const type = element.type;
        const specyficType = element.specyficType;
        if (type === ElementType.FRONT) {
            return state.selectedFront;
        }
        if (specyficType === SpecyficType.DRAWER) {
            return new ShaderSzuflada();
        }
        if (specyficType === SpecyficType.HANGER) {
            return new ShaderDrazek();
        }
        if (specyficType === SpecyficType.SHELF) {
            return state.selectedShelf;
        }
        return state.furniture[0];
    },
    shaderByName: () => (name: string) => {
        return all.find((shader: any) => shader.key === name)!;
    },
    furnitureShaders(state): IShader[] {
        return furniture;
    },
    rafixShaders(state): IShader[] {
        return rafix;
    },
    wallSupportShaders(state): IShader[] {
        return support;
    },
    selectedWoodShader(state): IShader | undefined {
        const furnitureShaders = [
            state.selectedBody,
            state.selectedFront,
            state.selectedShelf,
            state.selectedWall,
        ];
        return furnitureShaders.find((shader) =>
            woodShaders.includes(shader.key),
        );
    },
    selectedWoodShadersCount(state): number {
        const selectedShaders: IShader[] = [
            state.selectedHandle.shader,
            state.selectedFront,
            state.selectedWall,
            state.selectedShelf,
            state.selectedBody,
            state.selectedLegs.shader,
            state.selectedRafix,
            state.selectedSupport,
        ];
        return selectedShaders.filter((x) =>
            woodShaders.includes(x.key)).length;
    },
    isWoodShaderSelected: (state) => (type: SectionType): boolean => {
        const pairs: Array<Pair<IShader, SectionType>> = [
            new Pair(state.selectedBody, SectionType.BODY_COLOR),
            new Pair(state.selectedFront, SectionType.FRONT_COLOR),
            new Pair(state.selectedShelf, SectionType.SHELF_COLOR),
            new Pair(state.selectedWall, SectionType.WALL_COLOR),
        ];
        const shader = pairs.find((pair) => pair.y === type)!.x;
        return woodShaders.includes(shader.key);
    },
    isWoodShader: () => (shader: IShader): boolean => {
        return woodShaders.includes(shader.key);
    },
    isWoodShaderByName: () => (shaderName: string) => {
        return woodShaders.includes(shaderName);
    },
};

const whiteIndexes = [0, 1, 3, 6, 7];

const mutations: MutationTree<Shaders> = {
    selectHandle(state, handle: Handle) {
        state.selectedHandle = handle;
    },
    selectFront(state, shader: IShader) {
        state.selectedFront = shader;
    },
    selectWall(state, shader: IShader) {
        state.selectedWall = shader;

        const index = whiteIndexes.some(
            (i) => shader === furniture[i],
        ) ? 0 : 1;
        const rafixShader = rafix[index];
        state.selectedRafix = rafixShader;
    },
    selectShelf(state, shader: IShader) {
        state.selectedShelf = shader;
    },
    selectBody(state, shader: IShader) {
        state.selectedBody = shader;
    },
    selectLegs(state, legs: Legs) {
        state.selectedLegs = legs;
    },
    selectSupport(state, support: IShader) {
        state.selectedSupport = support;
    },
    selectSupportByShader(state, shader: IShader) {
        const index = whiteIndexes.some(
            (i) => shader === furniture[i],
        ) ? 0 : 1;
        const supportShader = support[index];
        state.selectedSupport = supportShader;
    },
    selectRafix(state, rafix: IShader) {
        state.selectedRafix = rafix;
    },
    selectRafixByShader(state, shader: IShader) {
        const index = whiteIndexes.some(
            (i) => shader === furniture[i],
        ) ? 0 : 1;
        const rafixShader = rafix[index];
        state.selectedRafix = rafixShader;
    },
    selectFrame(state, frame: IShader) {
        state.selectedFrame = frame;
    },
    restoreShaders(state, preset: ShadersPresetData) {
        state.selectedHandle =
            handles.find((handle: Handle) => handle.shader.key === preset.selectedHandleName)!;
        state.selectedLegs =
            legs.find((leg: Legs) =>
                leg.shader.key === preset.selectedLegsName &&
                leg.type === store.getters.product.type)!;
        state.selectedFront =
            furniture.find((shader: IShader) => shader.key === preset.selectedFrontName)!;
        state.selectedShelf =
            furniture.find((shader: IShader) => shader.key === preset.selectedShelfName)!;
        state.selectedWall =
            furniture.find((shader: IShader) => shader.key === preset.selectedWallName)!;
        state.selectedBody =
            furniture.find((shader: IShader) => shader.key === preset.selectedBodyName)!;
        state.selectedRafix =
            rafix.find((shader: IShader) => shader.key === preset.selectedRafixName)!;
        state.selectedSupport =
            support.find((shader: IShader) => shader.key === preset.selectedSupportName)!;
        state.selectedFrame =
            frame.find((shader: IShader) => shader.key === preset.selectedFrameName)!;
        if (!state.selectedFrame) { state.selectedFrame = new ShaderNull(); }
    },
};

const furniture = [
    new Shader6458(),
    new ShaderU8101(),
    new ShaderU899(),
    new ShaderU830(),
    new ShaderU606(),
    new ShaderU599(),
    new ShaderU163(),
    new ShaderH3157(),
    new ShaderH3396(),
    new ShaderH3162(),
    new ShaderH3325()
];

const rafix = [
    new Shader01Rafix(),
    new Shader02Rafix(),
];

const support = [
    new Shader01Podp(),
    new Shader02Podp(),
];

const frame = [
    new ShaderLiftedFrame(),
    new ShaderFloorFrame(),
    new ShaderFloorWithFlexibleSlatsFrame(),
];

const others = [
    new ShaderSzuflada(),
    new ShaderZawias(),
    new ShaderDrazek(),
    new ShaderNPodp(),
    new ShaderNBaza(),
    new ShaderKeaRing(),
    new ShaderTasma(),
];

const all: IShader[] = [];
all.push(...furniture);
all.push(...rafix);
all.push(...support);
all.push(...handles.map((handle) => handle.shader));
all.push(...legs.map((leg) => leg.shader));
all.push(...others);

const currentState: Shaders = {
    furniture,
    selectedHandle: handles[0],
    selectedFront: furniture[0],
    selectedWall: furniture[0],
    selectedShelf: furniture[0],
    selectedBody: furniture[0],
    selectedLegs: legs[0],
    selectedRafix: rafix[0],
    selectedSupport: support[0],
    selectedFrame: new ShaderNull(),
};

export const ShadersModule: Module<Shaders, RootState> = {
    state: currentState,
    getters,
    mutations,
};
